import { isEmpty } from 'es-toolkit/compat';
import { LDFlagSet } from 'launchdarkly-js-client-sdk';
import { useFlags } from 'launchdarkly-react-client-sdk';

export enum FeatureFlag {
    V2InformationRequest = 'FaasOrigEnableV2InformationRequest',
    PostApprovalInformationRequests = 'FaasOrigPostApprovalInformationRequests',
    V2InformationRequestPreApproval = 'FaasOrigUseV2InformationRequestPreApproval',
    BlocksApprovalEnforcement = 'FaasOrigBlocksApprovalEnforcementEnabled',
    ConditionAutoResolve = 'FaasOrigConditionAutoResolveEnabled',
    ConditionReviewTeam = 'FaasOrigOrpConditionReviewTeam',
    ConditionResolutionStatus = 'FaasOrigOrpConditionResolutionStatus',
    ConditionTextFormatting = 'FaasOrigConditionTextFormattingEnabled',
    CancelInformationRequest = 'FaasOrigEnableCancelInformationRequest',
    ConditionsWatcher = 'FaasOrigOrpConditionsWatcher',
    ConditionCreatorSections = 'FaasOrigOrpConditionCreatorSections',
    DddPendingPill = 'FaasOrigDddPendingPillEnabled',
    ManageTemplates = 'FaasOrigOrpManageTemplates',
    ConfigurableTriggers = 'FaasOrigOrpConfigurableTriggers',
    SubmissionsTab = 'FaasOrigOrpSubmissionsTab',
    SubmissionsTabCompare = 'FaasOrigOrpSubmissionsTabCompare',
    RenameResult = 'FaasOrigOrpRenameResultEnabled',
    SafeEmailViews = 'FaasOrigOrpSafeEmailViewsEnabled',
}

export enum FeatureFlagKeys {
    V2InformationRequest = 'isV2InformationRequestEnabled',
    PostApprovalInformationRequests = 'isPostApprovalInformationRequestsEnabled',
    V2InformationRequestPreApproval = 'isV2InformationRequestPreApprovalEnabled',
    BlocksApprovalEnforcement = 'isBlocksApprovalEnforcementEnabled',
    ConditionAutoResolve = 'isConditionAutoResolveEnabled',
    ConditionReviewTeam = 'isConditionReviewTeamEnabled',
    ConditionResolutionStatus = 'isConditionResolutionStatusEnabled',
    ConditionTextFormatting = 'isConditionTextFormattingEnabled',
    CancelInformationRequest = 'isCancelInformationRequestEnabled',
    ConditionsWatcher = 'isConditionsWatcherEnabled',
    ConditionCreatorSections = 'isConditionCreatorSectionsEnabled',
    DddPendingPill = 'isDddPendingPillEnabled',
    ManageTemplates = 'isManageTemplatesEnabled',
    ConfigurableTriggers = 'isConfigurableTriggersEnabled',
    SubmissionsTab = 'isSubmissionsTabEnabled',
    SubmissionsTabCompare = 'isSubmissionsTabCompareEnabled',
    RenameResult = 'isRenameResultEnabled',
    SafeEmailViews = 'isSafeEmailViewsEnabled',
}

export const bootstrapFlags: LDFlagSet = {
    [FeatureFlag.V2InformationRequest]: true,
    [FeatureFlag.PostApprovalInformationRequests]: false,
    [FeatureFlag.V2InformationRequestPreApproval]: false,
    [FeatureFlag.BlocksApprovalEnforcement]: false,
    [FeatureFlag.ConditionAutoResolve]: false,
    [FeatureFlag.ConditionReviewTeam]: false,
    [FeatureFlag.ConditionResolutionStatus]: false,
    [FeatureFlag.ConditionTextFormatting]: false,
    [FeatureFlag.CancelInformationRequest]: false,
    [FeatureFlag.ConditionsWatcher]: false,
    [FeatureFlag.ConditionCreatorSections]: false,
    [FeatureFlag.DddPendingPill]: false,
    [FeatureFlag.ManageTemplates]: false,
    [FeatureFlag.ConfigurableTriggers]: false,
    [FeatureFlag.SubmissionsTab]: false,
    [FeatureFlag.SubmissionsTabCompare]: false,
    [FeatureFlag.RenameResult]: false,
    [FeatureFlag.SafeEmailViews]: false,
};

export const isFeatureEnabled = (flags: LDFlagSet, flag: FeatureFlag): boolean => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const featureFlag: any = flags ? flags[flag] : null;
    const areFlagsEmpty = isEmpty(flags);

    if (areFlagsEmpty) {
        return bootstrapFlags[flag];
    }

    return featureFlag;
};

export const useFeatureFlags = (): Record<FeatureFlagKeys, boolean> => {
    const flags = useFlags();
    const newFlags = {} as Record<FeatureFlagKeys, boolean>;

    Object.entries(FeatureFlag).forEach(([key, value]) => {
        Object.assign(newFlags, {
            [FeatureFlagKeys[key as keyof typeof FeatureFlag]]: isFeatureEnabled(flags, value),
        });
    });

    return newFlags;
};
